import { useId, useMemo } from 'react';
import { cn } from '@/lib/utils';

interface Props {
  data: number[];
  width?: number;
  height?: number;
  tone?: 'positive' | 'negative' | 'neutral';
  className?: string;
  ariaLabel?: string;
}

/**
 * Pure-SVG sparkline. No deps. Subtle gradient fill, antialiased stroke.
 */
export function Sparkline({
  data,
  width = 96,
  height = 28,
  tone = 'neutral',
  className,
  ariaLabel = 'Trend sparkline',
}: Props) {
  const gradId = useId();

  const { path, area, last } = useMemo(() => {
    if (data.length === 0) return { path: '', area: '', last: 0 };
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    const stepX = width / Math.max(1, data.length - 1);

    const pts = data.map((v, i) => {
      const x = i * stepX;
      const y = height - ((v - min) / range) * (height - 4) - 2;
      return [x, y] as const;
    });

    const path = pts.reduce(
      (acc, [x, y], i) => acc + (i === 0 ? `M${x},${y}` : ` L${x},${y}`),
      '',
    );
    const lastX = pts[pts.length - 1]?.[0] ?? 0;
    const firstX = pts[0]?.[0] ?? 0;
    const area = `${path} L${lastX},${height} L${firstX},${height} Z`;
    return { path, area, last: pts[pts.length - 1]?.[1] ?? 0 };
  }, [data, width, height]);

  const stroke =
    tone === 'positive'
      ? 'hsl(var(--positive))'
      : tone === 'negative'
        ? 'hsl(var(--negative))'
        : 'hsl(var(--muted-foreground))';

  const lastX = data.length > 1 ? (width / (data.length - 1)) * (data.length - 1) : width;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      className={cn('overflow-visible', className)}
      role="img"
      aria-label={ariaLabel}
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.18" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradId})`} />
      <path
        d={path}
        fill="none"
        stroke={stroke}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      {data.length > 0 && (
        <circle cx={lastX} cy={last} r={2} fill={stroke} />
      )}
    </svg>
  );
}
